import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type AvailableAccount = {
    accountId: number;
    accountName: string;
    domain: string;
    employeeType: string;
    logoFileId: string | null;
    setupState: 'Provisioning' | 'Ready' | 'Failed';
    accessible?: boolean;
    accessState?: 'Active' | 'Ended' | 'Suspended' | 'Withdrawn';
};

type AuthTokens = {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: string;
    refreshTokenExpiresAt: string;
};

type UpdateTokensInput = AuthTokens & {
    availableAccounts?: AvailableAccount[];
};

type AuthState = {
    accessToken: string | null;
    refreshToken: string | null;
    accessTokenExpiresAt: string | null;
    refreshTokenExpiresAt: string | null;
    accountId: number | null;
    userId: number | null;
    availableAccounts: AvailableAccount[];
    permissions: string[];
    /**
     * Accounts the API has refused with `EMPLOYMENT_REVOKED` this session. The token's own account list
     * only catches up at the next refresh, so this is what lets the app react to the refusal now.
     */
    revokedAccountIds: number[];
    pausedAccountIds: number[];
    setAuth: (data: AuthTokens & { userId: number; availableAccounts: AvailableAccount[] }) => void;
    selectAccount: (accountId: number) => void;
    /** Marks one account as no longer the user's. Returns true when it was the selected account, which has been dropped. */
    markAccountRevoked: (accountId: number, paused?: boolean) => boolean;
    setPermissions: (permissions: string[]) => void;
    updateTokens: (data: UpdateTokensInput) => void;
    clearAccessToken: () => void;
    clearAuth: () => void;
    isAccessTokenExpired: () => boolean;
    isRefreshTokenExpired: () => boolean;
    hasPermission: (permission: string) => boolean;
};

function lostAccessIds(accounts: AvailableAccount[]) {
    const lost = accounts.filter((a) => a.accessible === false);
    return {
        revoked: lost.filter((a) => a.accessState === 'Ended' || a.accessState === 'Suspended').map((a) => a.accountId),
        paused: lost.filter((a) => a.accessState === 'Suspended').map((a) => a.accountId),
    };
}

function priorStillLost(ids: number[], accounts: AvailableAccount[]) {
    return ids.filter((id) => {
        const account = accounts.find((a) => a.accountId === id);
        return account === undefined || (account.accessible === false && account.accessState === undefined);
    });
}

const authStorageKey = 'auth-storage';

const storage = createJSONStorage(() => ({
    getItem: (name: string) => {
        return localStorage.getItem(name);
    },
    setItem: (name: string, value: string) => {
        localStorage.setItem(name, value);
    },
    removeItem: (name: string) => {
        localStorage.removeItem(name);
    }
}));

type PersistedAuth = Partial<AuthTokens> & { userId?: number | null; availableAccounts?: AvailableAccount[] };

function readPersistedAuth(): PersistedAuth | null {
    try {
        const raw = localStorage.getItem(authStorageKey);
        if (!raw) return null;
        return (JSON.parse(raw) as { state?: PersistedAuth }).state ?? null;
    } catch {
        return null;
    }
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            accessToken: null,
            refreshToken: null,
            accessTokenExpiresAt: null,
            refreshTokenExpiresAt: null,
            accountId: null,
            userId: null,
            availableAccounts: [],
            permissions: [],
            revokedAccountIds: [],
            pausedAccountIds: [],
            setAuth: (data) => {
                const lost = lostAccessIds(data.availableAccounts);
                set({
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    accessTokenExpiresAt: data.accessTokenExpiresAt,
                    refreshTokenExpiresAt: data.refreshTokenExpiresAt,
                    userId: data.userId,
                    availableAccounts: data.availableAccounts,
                    accountId: null,
                    revokedAccountIds: lost.revoked,
                    pausedAccountIds: lost.paused,
                });
            },
            selectAccount: (accountId) => set((state) => {
                const account = state.availableAccounts.find((a) => a.accountId === accountId);
                return account?.accessible === false ? {} : { accountId };
            }),
            markAccountRevoked: (accountId, paused = false) => {
                const wasSelected = get().accountId === accountId;
                set((state) => ({
                    // Only this account: the refresh token and every other membership stay as they are.
                    revokedAccountIds: state.revokedAccountIds.includes(accountId)
                        ? state.revokedAccountIds
                        : [...state.revokedAccountIds, accountId],
                    pausedAccountIds: paused
                        ? (state.pausedAccountIds.includes(accountId) ? state.pausedAccountIds : [...state.pausedAccountIds, accountId])
                        : state.pausedAccountIds.filter((id) => id !== accountId),
                    availableAccounts: state.availableAccounts.map((a) =>
                        a.accountId === accountId && a.accessible !== false ? { ...a, accessible: false } : a),
                    accountId: wasSelected ? null : state.accountId,
                    permissions: wasSelected ? [] : state.permissions,
                }));
                return wasSelected;
            },
            setPermissions: (permissions) => set({ permissions }),
            updateTokens: (data) => set((state) => {
                const accounts = data.availableAccounts ?? state.availableAccounts;
                const lost = lostAccessIds(accounts);
                const accountStillValid = state.accountId !== null
                    && accounts.some(a => a.accountId === state.accountId && a.accessible !== false);
                return {
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    accessTokenExpiresAt: data.accessTokenExpiresAt,
                    refreshTokenExpiresAt: data.refreshTokenExpiresAt,
                    availableAccounts: accounts,
                    accountId: accountStillValid ? state.accountId : null,
                    revokedAccountIds: [...new Set([...priorStillLost(state.revokedAccountIds, accounts), ...lost.revoked])],
                    pausedAccountIds: [...new Set([...priorStillLost(state.pausedAccountIds, accounts), ...lost.paused])],
                };
            }),
            clearAccessToken: () => set({
                accessToken: null,
                accessTokenExpiresAt: null,
            }),
            clearAuth: () => set({
                accessToken: null,
                refreshToken: null,
                accessTokenExpiresAt: null,
                refreshTokenExpiresAt: null,
                accountId: null,
                userId: null,
                availableAccounts: [],
                permissions: [],
                revokedAccountIds: [],
                pausedAccountIds: [],
            }),
            isAccessTokenExpired: () => {
                const expiresAt = get().accessTokenExpiresAt;
                if (!expiresAt) return true;
                return new Date(expiresAt).getTime() - 30000 < Date.now();
            },
            isRefreshTokenExpired: () => {
                const expiresAt = get().refreshTokenExpiresAt;
                if (!expiresAt) return true;
                return new Date(expiresAt).getTime() < Date.now();
            },
            hasPermission: (permission) => {
                const state = get();
                if (!state.permissions) return false;
                return state.permissions.includes(permission);
            },
        }),
        {
            name: authStorageKey,
            storage,
        }
    )
);

export function adoptNewerPersistedTokens(): void {
    const persisted = readPersistedAuth();
    const current = useAuthStore.getState();
    if (
        !persisted?.accessToken
        || !persisted.refreshToken
        || !persisted.accessTokenExpiresAt
        || !persisted.refreshTokenExpiresAt
        || !current.refreshToken
        || current.userId === null
        || persisted.userId !== current.userId
    ) {
        return;
    }
    const currentExpiry = current.accessTokenExpiresAt ? Date.parse(current.accessTokenExpiresAt) : 0;
    if (Date.parse(persisted.accessTokenExpiresAt) <= currentExpiry) return;
    current.updateTokens({
        accessToken: persisted.accessToken,
        refreshToken: persisted.refreshToken,
        accessTokenExpiresAt: persisted.accessTokenExpiresAt,
        refreshTokenExpiresAt: persisted.refreshTokenExpiresAt,
        availableAccounts: persisted.availableAccounts,
    });
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key === authStorageKey) adoptNewerPersistedTokens();
    });
}
